import { useState } from "react";
import { Link } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import PageMeta from "../components/PageMeta";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";

/* ─── TYPES ──────────────────────────────────────────────────────────── */
interface Answers {
  petName: string;
  petSpecies: string;
  petBreed: string;
  petAge: string;
  petCount: string;
  currentVetSetup: string;
  vetSatisfaction: string;
  vetVisitFrequency: string;
  biggestFrustrations: string[];
  annualSpend: string;
  membershipInterest: string;
  mostImportantFeature: string;
  genomicsInterest: string;
  telehealthInterest: string;
  emergencyCareImportance: string;
  designImportance: string;
  longevityTreatmentInterest: string;
  longevityPriceThreshold: string;
  hasInsurance: string;
  whyNoInsurance: string[];
  endOfLifeInsurancePrice: string;
  emergencyFinancialReady: string;
  vetWishlist: string[];
  vetWishlistOther: string;
  howHeard: string;
  singaporeArea: string;
  email: string;
  whatsapp: string;
}

const EMPTY: Answers = {
  petName: "", petSpecies: "", petBreed: "", petAge: "", petCount: "",
  currentVetSetup: "", vetSatisfaction: "", vetVisitFrequency: "",
  biggestFrustrations: [], annualSpend: "", membershipInterest: "",
  mostImportantFeature: "", genomicsInterest: "", telehealthInterest: "",
  emergencyCareImportance: "", designImportance: "",
  longevityTreatmentInterest: "", longevityPriceThreshold: "",
  hasInsurance: "", whyNoInsurance: [], endOfLifeInsurancePrice: "",
  emergencyFinancialReady: "",
  vetWishlist: [], vetWishlistOther: "",
  howHeard: "", singaporeArea: "", email: "", whatsapp: "",
};

/* ─── STEP DEFINITIONS ─────────────────────────────────────────────── */
const STEPS = [
  "The Main Character",
  "The Status Quo",
  "The Money Talk",
  "The Wish List",
  "The Future",
  "About You",
];

/* ─── SMALL REUSABLE BITS ─────────────────────────────────────────── */
function OptionCard({
  value, label, sub, selected, onClick,
}: {
  value: string; label: string; sub?: string; selected: boolean; onClick: () => void;
}) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileTap={{ scale: 0.97 }}
      className={`w-full text-left px-5 py-4 border rounded-[14px] transition-all duration-300 ${
        selected
          ? "border-primary bg-primary/20"
          : "border-foreground/10 hover:border-primary/40 hover:bg-primary/5 bg-transparent"
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`mt-0.5 w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-all duration-300 ${
            selected ? "border-primary bg-primary" : "border-foreground/25"
          }`}
        >
          {selected && <div className="w-1.5 h-1.5 rounded-full bg-foreground" />}
        </div>
        <div>
          <p className={`font-sans text-sm leading-snug transition-colors duration-300 ${selected ? "text-foreground" : ""}`}>{label}</p>
          {sub && <p className="font-sans text-xs opacity-40 mt-0.5 leading-snug">{sub}</p>}
        </div>
      </div>
    </motion.button>
  );
}

function CheckCard({
  value, label, sub, selected, onClick,
}: {
  value: string; label: string; sub?: string; selected: boolean; onClick: () => void;
}) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileTap={{ scale: 0.97 }}
      className={`w-full text-left px-5 py-4 border rounded-[14px] transition-all duration-300 ${
        selected
          ? "border-primary bg-primary/20"
          : "border-foreground/10 hover:border-primary/40 hover:bg-primary/5 bg-transparent"
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`mt-0.5 w-4 h-4 rounded border-2 flex-shrink-0 flex items-center justify-center transition-all duration-300 ${
            selected ? "border-primary bg-primary" : "border-foreground/25"
          }`}
        >
          {selected && <Check className="w-2.5 h-2.5 text-foreground" />}
        </div>
        <div>
          <p className={`font-sans text-sm leading-snug transition-colors duration-300 ${selected ? "text-foreground" : ""}`}>{label}</p>
          {sub && <p className="font-sans text-xs opacity-40 mt-0.5 leading-snug">{sub}</p>}
        </div>
      </div>
    </motion.button>
  );
}

function TextInput({
  label, value, onChange, placeholder, optional,
}: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; optional?: boolean;
}) {
  return (
    <div className="flex flex-col gap-2">
      <label className="font-grotesk text-[10px] uppercase tracking-widest opacity-50">
        {label}{optional && <span className="ml-1 opacity-60">(optional)</span>}
      </label>
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="bg-transparent border-b border-foreground/20 py-3 font-sans text-sm focus:outline-none focus:border-primary transition-colors duration-300 placeholder:opacity-25"
      />
    </div>
  );
}

function ScaleRow({
  value, onChange, lo, hi,
}: {
  value: string; onChange: (v: string) => void; lo: string; hi: string;
}) {
  const opts = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"];
  return (
    <div>
      <div className="flex gap-2 mb-3">
        {opts.map(n => (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            className={`flex-1 py-3 font-sans text-sm border rounded-[10px] transition-all duration-200 ${
              value === n
                ? "bg-foreground text-background border-foreground"
                : "border-foreground/15 hover:border-primary hover:bg-primary/10"
            }`}
          >
            {n}
          </button>
        ))}
      </div>
      <div className="flex justify-between font-grotesk text-[9px] uppercase tracking-widest opacity-35">
        <span>{lo}</span>
        <span>{hi}</span>
      </div>
    </div>
  );
}

/* ─── SLIDE ANIMATION ─────────────────────────────────────────────── */
const slideVariants = {
  enter: (dir: number) => ({ x: dir > 0 ? 60 : -60, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir: number) => ({ x: dir > 0 ? -60 : 60, opacity: 0 }),
};

/* ─── MAIN COMPONENT ──────────────────────────────────────────────── */
export default function Survey() {
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [answers, setAnswers] = useState<Answers>(EMPTY);
  const [done, setDone] = useState(false);

  const set = (k: keyof Answers) => (v: string) =>
    setAnswers(a => ({ ...a, [k]: v }));

  const toggleFrustration = (v: string) =>
    setAnswers(a => ({
      ...a,
      biggestFrustrations: a.biggestFrustrations.includes(v)
        ? a.biggestFrustrations.filter(x => x !== v)
        : [...a.biggestFrustrations, v],
    }));

  const toggleWhyNoInsurance = (v: string) =>
    setAnswers(a => ({
      ...a,
      whyNoInsurance: a.whyNoInsurance.includes(v)
        ? a.whyNoInsurance.filter(x => x !== v)
        : [...a.whyNoInsurance, v],
    }));

  const toggleVetWishlist = (v: string) =>
    setAnswers(a => ({
      ...a,
      vetWishlist: a.vetWishlist.includes(v)
        ? a.vetWishlist.filter(x => x !== v)
        : [...a.vetWishlist, v],
    }));

  const go = async (delta: number) => {
    setDir(delta);
    if (step + delta >= STEPS.length) {
      try {
        await fetch("/api/survey", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(answers),
        });
      } catch (e) {
        console.error("Survey submission failed:", e);
      }
      setDone(true);
      return;
    }
    setStep(s => Math.max(0, Math.min(STEPS.length - 1, s + delta)));
  };

  const progress = ((step + 1) / STEPS.length) * 100;

  if (done) return <ThankYou petName={answers.petName} />;

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      <PageMeta
        title="Pet Health Survey — Furbaby Vet Care"
        description="Help us understand your pet's health history so Furbaby can personalise their care plan from day one."
        canonical="https://furbaby.vet/main/survey"
        ogTitle="Furbaby Pet Health Survey"
        ogDescription="Tell us about your furchild so we can build a care plan tailored to them."
      />

      {/* Nav + hover-reveal step labels */}
      <div className="group/nav fixed top-0 left-0 right-0 z-50">
        <nav className="px-6 md:px-12 py-5 flex justify-between items-center bg-background/90 backdrop-blur-sm border-b border-foreground/6">
          <Link href="/" className="group flex items-center gap-2 font-grotesk text-xs uppercase tracking-widest hover:opacity-60 transition-opacity duration-500">
            <ArrowLeft className="w-3 h-3 group-hover:-translate-x-1 transition-transform duration-400" />
            Home
          </Link>
          <span className="font-serif text-2xl font-bold italic tracking-tighter">Furbaby</span>
          <span className="font-grotesk text-[10px] uppercase tracking-widest opacity-35">
            {step + 1} / {STEPS.length}
          </span>
        </nav>

        {/* Progress bar */}
        <div className="h-[2px] bg-foreground/8">
          <motion.div
            className="h-full bg-primary"
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          />
        </div>

        {/* Step labels — desktop, hidden until nav hover */}
        <div className="hidden md:flex justify-between px-6 pt-3 pb-2 bg-background/95 backdrop-blur-sm border-b border-foreground/5
          opacity-0 -translate-y-1 pointer-events-none
          group-hover/nav:opacity-100 group-hover/nav:translate-y-0 group-hover/nav:pointer-events-auto
          transition-all duration-300 ease-out">
          {STEPS.map((s, i) => (
            <span
              key={i}
              className={`font-grotesk text-[9px] uppercase tracking-wide transition-all duration-300 ${
                i === step ? "text-primary opacity-100" : i < step ? "opacity-40" : "opacity-15"
              }`}
            >
              {s}
            </span>
          ))}
        </div>
      </div>

      {/* Body */}
      <div className="max-w-2xl mx-auto px-6 md:px-12 pt-28 md:pt-32 pb-32">
        <AnimatePresence mode="wait" custom={dir}>
          <motion.div
            key={step}
            custom={dir}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.42, ease: [0.16, 1, 0.3, 1] }}
          >
            {step === 0 && <Step0 answers={answers} set={set} />}
            {step === 1 && <Step1 answers={answers} set={set} toggle={toggleFrustration} />}
            {step === 2 && <Step2 answers={answers} set={set} />}
            {step === 3 && <Step3 answers={answers} set={set} />}
            {step === 4 && <Step4 answers={answers} set={set} toggle={toggleWhyNoInsurance} />}
            {step === 5 && <Step5 answers={answers} set={set} toggle={toggleVetWishlist} />}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Footer nav */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-background/90 backdrop-blur-sm border-t border-foreground/8 px-6 md:px-12 py-5 flex justify-between items-center">
        <button
          onClick={() => go(-1)}
          disabled={step === 0}
          className="group flex items-center gap-2 font-grotesk text-xs uppercase tracking-widest opacity-50 hover:opacity-100 transition-opacity duration-300 disabled:opacity-15 disabled:cursor-not-allowed"
        >
          <ArrowLeft className="w-3 h-3 group-hover:-translate-x-1 transition-transform duration-300" />
          Back
        </button>

        <div className="flex gap-1.5">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className={`rounded-full transition-all duration-300 ${
                i === step ? "w-5 h-1.5 bg-primary" : i < step ? "w-1.5 h-1.5 bg-primary/40" : "w-1.5 h-1.5 bg-foreground/15"
              }`}
            />
          ))}
        </div>

        <button
          onClick={() => go(1)}
          className="group flex items-center gap-3 font-grotesk text-xs uppercase tracking-widest px-5 py-2.5 rounded-full bg-foreground text-background hover:bg-primary hover:text-foreground transition-all duration-300"
        >
          {step === STEPS.length - 1 ? "Submit" : "Continue"}
          <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform duration-300" />
        </button>
      </div>
    </div>
  );
}

/* ─── STEP 0: THE MAIN CHARACTER ──────────────────────────────────── */
function Step0({ answers, set }: { answers: Answers; set: (k: keyof Answers) => (v: string) => void }) {
  return (
    <div>
      <p className="font-grotesk text-[10px] uppercase tracking-widest opacity-40 mb-4">Step 1 · The Main Character</p>
      <h2 className="font-serif text-4xl md:text-5xl tracking-tighter leading-tight mb-3">
        Before we begin,<br />
        <span className="italic opacity-70">let's establish who's</span><br />
        really in charge.
      </h2>
      <p className="font-sans text-sm opacity-50 leading-relaxed mb-10 max-w-sm">
        We're going to ask you some questions. Technically they're about you, but let's be honest — they're about your pet.
      </p>

      <div className="flex flex-col gap-8">
        <TextInput label="Your pet's name" value={answers.petName} onChange={set("petName")} placeholder="Biscuit, Sir Fluffington, etc." />

        <div>
          <p className="font-grotesk text-[10px] uppercase tracking-widest opacity-50 mb-3">Species</p>
          <div className="grid grid-cols-2 gap-3">
            {[
              { v: "dog", l: "Dog", s: "Good boy energy confirmed" },
              { v: "cat", l: "Cat", s: "The superior species, we know" },
              { v: "both", l: "Both", s: "A household of chaos and joy" },
              { v: "other", l: "Other", s: "Exotic and we love that for you" },
            ].map(o => (
              <OptionCard key={o.v} value={o.v} label={o.l} sub={o.s} selected={answers.petSpecies === o.v} onClick={() => set("petSpecies")(o.v)} />
            ))}
          </div>
        </div>

        <TextInput label="Breed" value={answers.petBreed} onChange={set("petBreed")} placeholder="e.g. British Shorthair, Savannah, Golden…" optional />

        <div>
          <p className="font-grotesk text-[10px] uppercase tracking-widest opacity-50 mb-3">Age</p>
          <div className="grid grid-cols-2 gap-3">
            {[
              { v: "under1", l: "Under 1 year", s: "A baby. An absolute baby." },
              { v: "1to3", l: "1–3 years", s: "In their chaotic-good era" },
              { v: "4to7", l: "4–7 years", s: "Peak floof, peak personality" },
              { v: "8to12", l: "8–12 years", s: "Senior royalty — treat accordingly" },
              { v: "over12", l: "12+ years", s: "A legend. An institution." },
            ].map(o => (
              <OptionCard key={o.v} value={o.v} label={o.l} sub={o.s} selected={answers.petAge === o.v} onClick={() => set("petAge")(o.v)} />
            ))}
          </div>
        </div>

        <div>
          <p className="font-grotesk text-[10px] uppercase tracking-widest opacity-50 mb-3">How many pets does your household contain?</p>
          <div className="flex flex-col gap-2">
            {[
              { v: "1", l: "Just the one", s: "An only child. Spoiled accordingly." },
              { v: "2", l: "Two", s: "A healthy pair. They definitely get along fine." },
              { v: "3", l: "Three", s: "Bold. Chaotic. Respectable." },
              { v: "4plus", l: "Four or more", s: "You're either a saint or you need a spreadsheet." },
            ].map(o => (
              <OptionCard key={o.v} value={o.v} label={o.l} sub={o.s} selected={answers.petCount === o.v} onClick={() => set("petCount")(o.v)} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── STEP 1: THE STATUS QUO ─────────────────────────────────────── */
function Step1({
  answers, set, toggle,
}: {
  answers: Answers;
  set: (k: keyof Answers) => (v: string) => void;
  toggle: (v: string) => void;
}) {
  return (
    <div>
      <p className="font-grotesk text-[10px] uppercase tracking-widest opacity-40 mb-4">Step 2 · The Status Quo</p>
      <h2 className="font-serif text-4xl md:text-5xl tracking-tighter leading-tight mb-3">
        Tell us about<br />
        <span className="italic opacity-70">the vet situation.</span>
      </h2>
      <p className="font-sans text-sm opacity-50 leading-relaxed mb-10 max-w-sm">
        No judgment. We've all had that vet visit where the waiting room smelled like existential dread and you left with a bill but no answers.
      </p>

      <div className="flex flex-col gap-10">
        <div>
          <p className="font-grotesk text-[10px] uppercase tracking-widest opacity-50 mb-3">Which best describes your current vet arrangement?</p>
          <div className="flex flex-col gap-2">
            {[
              { v: "loyal", l: "One vet, always", s: "A relationship built on trust and mild anxiety" },
              { v: "chain", l: "One of the big chains", s: "Mount Pleasant, VES, PetsAvenue — you know the ones" },
              { v: "whoever", l: "Whoever's available", s: "Loyalty is a concept for people who haven't waited 45 minutes" },
              { v: "emergency", l: "Emergency visits only", s: "We go when things get dramatic. Which is often." },
              { v: "none", l: "No regular vet currently", s: "A gap we'd like to help with" },
            ].map(o => (
              <OptionCard key={o.v} value={o.v} label={o.l} sub={o.s} selected={answers.currentVetSetup === o.v} onClick={() => set("currentVetSetup")(o.v)} />
            ))}
          </div>
        </div>

        <div>
          <p className="font-grotesk text-[10px] uppercase tracking-widest opacity-50 mb-3">How satisfied are you with your current vet care?</p>
          <p className="font-sans text-xs opacity-35 mb-4">1 = "I could do better with a YouTube video" · 10 = "They know my pet's star sign"</p>
          <ScaleRow value={answers.vetSatisfaction} onChange={set("vetSatisfaction")} lo="Not at all" hi="Completely" />
        </div>

        <div>
          <p className="font-grotesk text-[10px] uppercase tracking-widest opacity-50 mb-3">How often does {answers.petName || "your pet"} visit the vet?</p>
          <div className="flex flex-col gap-2">
            {[
              { v: "monthly", l: "Monthly or more", s: "Dedicated. Or your pet has developed a personality disorder. Both are valid." },
              { v: "quarterly", l: "Every few months", s: "The conscientious middle ground. The vet recognises you but still checks the chart." },
              { v: "annually", l: "Once a year", s: "In, out, bill, denial. The annual ritual." },
              { v: "emergency", l: "Only when something goes wrong", s: "Reactive, not preventive. You and about 65% of Singapore." },
              { v: "rarely", l: "Rarely or never", s: "Living your best risk-tolerant life. Or blissful denial. Either is fine." },
            ].map(o => (
              <OptionCard key={o.v} value={o.v} label={o.l} sub={o.s} selected={answers.vetVisitFrequency === o.v} onClick={() => set("vetVisitFrequency")(o.v)} />
            ))}
          </div>
        </div>

        <div>
          <p className="font-grotesk text-[10px] uppercase tracking-widest opacity-50 mb-3">What frustrates you most about current vet care? <span className="opacity-60">(select all that apply)</span></p>
          <div className="flex flex-col gap-2">
            {[
              { v: "waits", l: "The waiting times", s: "Forty-five minutes. For a five-minute visit." },
              { v: "price", l: "Unpredictable pricing", s: "The bill arrives like a plot twist" },
              { v: "noknow", l: "Staff who don't know my pet", s: "They've met Biscuit eleven times. They still ask if he's new." },
              { v: "clinical", l: "Clinical, cold environment", s: "Fluorescent lights and industrial linoleum: a masterclass in anxiety" },
              { v: "continuity", l: "No continuity between visits", s: "Every visit starts from scratch" },
              { v: "nogenomic", l: "No preventive or genomic insight", s: "We don't find out about health risks until they become health problems" },
              { v: "telehealth", l: "Can't get a quick answer online", s: "A 3-minute question requires a 60-minute appointment" },
              { v: "nothing", l: "Actually, I have no complaints", s: "Doing great. We admire you." },
            ].map(o => (
              <CheckCard key={o.v} value={o.v} label={o.l} sub={o.s} selected={answers.biggestFrustrations.includes(o.v)} onClick={() => toggle(o.v)} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── STEP 2: THE MONEY TALK ─────────────────────────────────────── */
function Step2({ answers, set }: { answers: Answers; set: (k: keyof Answers) => (v: string) => void }) {
  return (
    <div>
      <p className="font-grotesk text-[10px] uppercase tracking-widest opacity-40 mb-4">Step 3 · The Money Talk</p>
      <h2 className="font-serif text-4xl md:text-5xl tracking-tighter leading-tight mb-3">
        We promise<br />
        <span className="italic opacity-70">to be gentle.</span>
      </h2>
      <p className="font-sans text-sm opacity-50 leading-relaxed mb-10 max-w-sm">
        This section is about spending. We know. We'll be quick. And no, we're not going to judge you for the emergency dental procedure at 11pm on a Saturday.
      </p>

      <div className="flex flex-col gap-10">
        <div>
          <p className="font-grotesk text-[10px] uppercase tracking-widest opacity-50 mb-3">How much do you currently spend on vet care per year, per pet?</p>
          <div className="flex flex-col gap-2">
            {[
              { v: "under500", l: "Under S$500", s: "Annual check-up, one panicked Google at 2am, and a prayer that nothing escalates." },
              { v: "500to1500", l: "S$500 – S$1,500", s: "Responsible, consistent, and still a little surprised by the bill every single time." },
              { v: "1500to3000", l: "S$1,500 – S$3,000", s: "You take this seriously. Your pet has better healthcare than most of your colleagues." },
              { v: "3000to6000", l: "S$3,000 – S$6,000", s: "Devoted. The vet knows your name, your coffee order, and your pet's anxious attachment style." },
              { v: "over6000", l: "Over S$6,000", s: "No comment required. You are the target audience. Welcome home." },
            ].map(o => (
              <OptionCard key={o.v} value={o.v} label={o.l} sub={o.s} selected={answers.annualSpend === o.v} onClick={() => set("annualSpend")(o.v)} />
            ))}
          </div>
        </div>

        <div>
          <p className="font-grotesk text-[10px] uppercase tracking-widest opacity-50 mb-3">
            Furbaby membership starts at S$680 per year — covering consultations, priority booking, loyalty discounts, and genomics screening. How does that sit with you?
          </p>
          <div className="flex flex-col gap-2">
            {[
              { v: "yes_definitely", l: "Sign me up immediately", s: "The waitlist is right this way." },
              { v: "yes_probably", l: "Very likely, once I know more", s: "A sensible human. We respect this." },
              { v: "maybe", l: "Possibly — depends on the details", s: "Fair. That's why you're here." },
              { v: "probably_not", l: "Probably not at that price", s: "Honest feedback. We appreciate it." },
              { v: "no", l: "No — vet memberships aren't for me", s: "Totally valid. We're glad you took the survey anyway." },
            ].map(o => (
              <OptionCard key={o.v} value={o.v} label={o.l} sub={o.s} selected={answers.membershipInterest === o.v} onClick={() => set("membershipInterest")(o.v)} />
            ))}
          </div>
        </div>

        <div>
          <p className="font-grotesk text-[10px] uppercase tracking-widest opacity-50 mb-3">If you were choosing a vet membership, which single thing would matter most?</p>
          <div className="flex flex-col gap-2">
            {[
              { v: "continuity", l: "Continuity — a vet who actually knows my pet", s: "Name, age, quirks, dietary habits, irrational fears. The full picture." },
              { v: "design", l: "The environment — calm, clean, beautiful", s: "Studies agree. So does anyone who's ever sat under fluorescent lighting." },
              { v: "price", l: "Value for money — predictable annual cost", s: "No surprise invoices. Three of the most beautiful words in the language." },
              { v: "genomics", l: "Proactive health — screening, genomics, prevention", s: "Getting ahead of problems before they start. Revolutionary concept, apparently." },
              { v: "emergency", l: "Emergency access — 24H care when it matters", s: "Knowing someone picks up at 3am changes everything. Even if you never call." },
              { v: "telehealth", l: "Telehealth — quick answers without a clinic visit", s: "Is this lump normal? Probably. But you'd very much like to know for certain." },
            ].map(o => (
              <OptionCard key={o.v} value={o.v} label={o.l} sub={o.s} selected={answers.mostImportantFeature === o.v} onClick={() => set("mostImportantFeature")(o.v)} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── STEP 3: THE WISH LIST ──────────────────────────────────────── */
function Step3({ answers, set }: { answers: Answers; set: (k: keyof Answers) => (v: string) => void }) {
  return (
    <div>
      <p className="font-grotesk text-[10px] uppercase tracking-widest opacity-40 mb-4">Step 4 · The Wish List</p>
      <h2 className="font-serif text-4xl md:text-5xl tracking-tighter leading-tight mb-3">
        This is where<br />
        <span className="italic opacity-70">we get specific.</span>
      </h2>
      <p className="font-sans text-sm opacity-50 leading-relaxed mb-10 max-w-sm">
        Some of what we're building doesn't exist anywhere in Singapore yet. We'd love to know if it should.
      </p>

      <div className="flex flex-col gap-10">
        <div>
          <p className="font-sans text-sm mb-1">
            <strong>FurGenomics:</strong> a DNA health screening service for your pet — breed-specific disease risk panels, metabolic profiling, and a personalised longevity report. Priced from S$98.
          </p>
          <p className="font-grotesk text-[10px] uppercase tracking-widest opacity-50 mb-3 mt-5">How interested are you?</p>
          <p className="font-sans text-xs opacity-35 mb-4">1 = "Science is fine, but Biscuit's fine" · 10 = "Take my DNA and Biscuit's DNA immediately"</p>
          <ScaleRow value={answers.genomicsInterest} onChange={set("genomicsInterest")} lo="Not interested" hi="Very interested" />
        </div>

        <div>
          <p className="font-sans text-sm mb-1">
            <strong>Telehealth consultations:</strong> video calls with a Furbaby vet for non-emergency questions, prescription renewals, and post-procedure follow-up — included in Besotted membership, add-on for others.
          </p>
          <p className="font-grotesk text-[10px] uppercase tracking-widest opacity-50 mb-3 mt-5">How useful would this be for you?</p>
          <p className="font-sans text-xs opacity-35 mb-4">1 = "I prefer the in-person drama" · 10 = "This would save my life (and Biscuit's)"</p>
          <ScaleRow value={answers.telehealthInterest} onChange={set("telehealthInterest")} lo="Not useful" hi="Extremely useful" />
        </div>

        <div>
          <p className="font-grotesk text-[10px] uppercase tracking-widest opacity-50 mb-3">How important is 24-hour emergency care access to you?</p>
          <div className="flex flex-col gap-2">
            {[
              { v: "critical", l: "Critical — I need to know someone is there at 3am", s: "You've been awake Googling symptoms at midnight. We know. Phase 2 is for you." },
              { v: "important", l: "Important — ideally yes, but not a dealbreaker", s: "The reasonable majority. You'd use it. You've just survived without it. Barely." },
              { v: "niceToHave", l: "Nice to have — I'd use it if available", s: "A comfort, not a requirement. You've never panic-called a vet at midnight. Yet." },
              { v: "notNeeded", l: "Not needed — I've managed fine without it", s: "The stoic pet owner. Either your pet is indestructible or you have excellent instincts." },
            ].map(o => (
              <OptionCard key={o.v} value={o.v} label={o.l} sub={o.s} selected={answers.emergencyCareImportance === o.v} onClick={() => set("emergencyCareImportance")(o.v)} />
            ))}
          </div>
        </div>

        <div>
          <p className="font-grotesk text-[10px] uppercase tracking-widest opacity-50 mb-3">
            We're designing a clinic that looks nothing like a vet clinic. Think: natural materials, warm light, a place you'd actually want to sit in. How much does that matter to you?
          </p>
          <div className="flex flex-col gap-2">
            {[
              { v: "alot", l: "Enormously — the environment changes everything", s: "You noticed the lighting in this sentence. The aesthete. Our people." },
              { v: "somewhat", l: "It matters — I'd notice the difference", s: "Walking into a room that feels calm does something to your nervous system. And theirs." },
              { v: "abit", l: "Slightly — nice but not a deciding factor", s: "Clinical quality first. We get it. We'll still make it beautiful, though. You're welcome." },
              { v: "notatall", l: "Not at all — I just want good veterinary care", s: "A purist. We respect this. We'll quietly disagree about the linoleum, but we respect it." },
            ].map(o => (
              <OptionCard key={o.v} value={o.v} label={o.l} sub={o.s} selected={answers.designImportance === o.v} onClick={() => set("designImportance")(o.v)} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── STEP 4: THE FUTURE ─────────────────────────────────────────── */
function Step4({
  answers, set, toggle,
}: {
  answers: Answers;
  set: (k: keyof Answers) => (v: string) => void;
  toggle: (v: string) => void;
}) {
  return (
    <div>
      <p className="font-grotesk text-[10px] uppercase tracking-widest opacity-40 mb-4">Step 5 · The Future</p>
      <h2 className="font-serif text-4xl md:text-5xl tracking-tighter leading-tight mb-3">
        Medicine is<br />
        <span className="italic opacity-70">becoming personal.</span>
      </h2>
      <p className="font-sans text-sm opacity-50 leading-relaxed mb-10 max-w-sm">
        Veterinary science is going through the same revolution human medicine went through — genomics, personalised protocols, and proactive longevity care. We'd like to know how you feel about that.
      </p>

      <div className="flex flex-col gap-10">

        {/* Longevity treatment */}
        <div>
          <p className="font-sans text-sm mb-1">
            <strong>Longevity medicine:</strong> imagine a monthly treatment — scientifically validated — that extended your pet's healthy lifespan by 20–30%. Not speculative. Not a supplement. A genuine medical intervention.
          </p>
          <p className="font-grotesk text-[10px] uppercase tracking-widest opacity-50 mb-3 mt-5">Would you use it?</p>
          <div className="flex flex-col gap-2">
            {[
              { v: "yes_immediately", l: "Absolutely — take my money", s: "More years with them. Non-negotiable." },
              { v: "yes_probably", l: "Very likely, assuming it's safe and evidence-based", s: "The sensible optimist" },
              { v: "maybe", l: "Possibly — I'd want to understand the research first", s: "Fair. Science should earn trust." },
              { v: "probably_not", l: "Probably not — I'm not sure I'd intervene this way", s: "A philosophical position we respect" },
              { v: "no", l: "No — I'd rather let nature take its course", s: "A dignified stance. Also valid." },
            ].map(o => (
              <OptionCard key={o.v} value={o.v} label={o.l} sub={o.s} selected={answers.longevityTreatmentInterest === o.v} onClick={() => set("longevityTreatmentInterest")(o.v)} />
            ))}
          </div>
        </div>

        {answers.longevityTreatmentInterest && !["no", "probably_not"].includes(answers.longevityTreatmentInterest) && (
          <div>
            <p className="font-grotesk text-[10px] uppercase tracking-widest opacity-50 mb-3">What monthly price would feel reasonable for a treatment like this?</p>
            <div className="flex flex-col gap-2">
              {[
                { v: "under50", l: "Under S$50/month", s: "Affordable tier — broad accessibility" },
                { v: "50to100", l: "S$50–S$100/month", s: "The considered investment" },
                { v: "100to200", l: "S$100–S$200/month", s: "If the evidence is there, yes" },
                { v: "200to400", l: "S$200–S$400/month", s: "A premium intervention — and worth it" },
                { v: "over400", l: "Over S$400/month", s: "Cost is not the primary consideration" },
              ].map(o => (
                <OptionCard key={o.v} value={o.v} label={o.l} sub={o.s} selected={answers.longevityPriceThreshold === o.v} onClick={() => set("longevityPriceThreshold")(o.v)} />
              ))}
            </div>
          </div>
        )}

        {/* Insurance */}
        <div>
          <p className="font-grotesk text-[10px] uppercase tracking-widest opacity-50 mb-3">Does {answers.petName || "your pet"} currently have health insurance?</p>
          <div className="flex flex-col gap-2">
            {[
              { v: "yes_comprehensive", l: "Yes — comprehensive cover", s: "You're prepared. Good." },
              { v: "yes_basic", l: "Yes — basic cover only", s: "Something is better than nothing" },
              { v: "no_considered", l: "No — but I've thought about it", s: "The road not yet taken" },
              { v: "no_never", l: "No — I've never really looked into it", s: "That's exactly what we're here for" },
            ].map(o => (
              <OptionCard key={o.v} value={o.v} label={o.l} sub={o.s} selected={answers.hasInsurance === o.v} onClick={() => set("hasInsurance")(o.v)} />
            ))}
          </div>
        </div>

        {answers.hasInsurance && answers.hasInsurance.startsWith("no") && (
          <div>
            <p className="font-grotesk text-[10px] uppercase tracking-widest opacity-50 mb-3">Why not? <span className="opacity-60">(select all that apply)</span></p>
            <div className="flex flex-col gap-2">
              {[
                { v: "too_expensive", l: "It's too expensive", s: "The most common reason. We hear it." },
                { v: "too_complex", l: "Too complicated — I don't know where to start", s: "Insurers have not made this easy" },
                { v: "low_value", l: "I've looked — the coverage isn't worth it", s: "Exclusions. Limits. Fine print." },
                { v: "healthy_pet", l: "My pet is young and healthy — it feels unnecessary", s: "The risk tolerance position" },
                { v: "self_insure", l: "I'd rather save the money myself", s: "The disciplined alternative" },
                { v: "didnt_know", l: "I didn't know pet insurance was available here", s: "It is. It's just not great yet." },
              ].map(o => (
                <CheckCard key={o.v} value={o.v} label={o.l} sub={o.s} selected={answers.whyNoInsurance.includes(o.v)} onClick={() => toggle(o.v)} />
              ))}
            </div>
          </div>
        )}

        <div>
          <p className="font-sans text-sm mb-1">
            <strong>Furbaby's model:</strong> instead of traditional insurance with exclusions, waiting periods, and claim disputes — we're building a membership that includes end-of-life care. No surprise bills when it matters most. The final chapter is covered.
          </p>
          <p className="font-grotesk text-[10px] uppercase tracking-widest opacity-50 mb-3 mt-5">What monthly amount would feel worth it for that guarantee?</p>
          <div className="flex flex-col gap-2">
            {[
              { v: "under80", l: "Under S$80/month", s: "Entry-level access — if it's genuinely covered" },
              { v: "80to150", l: "S$80–S$150/month", s: "The considered commitment" },
              { v: "150to300", l: "S$150–S$300/month", s: "Premium peace of mind" },
              { v: "300to500", l: "S$300–S$500/month", s: "If the coverage is comprehensive and real" },
              { v: "over500", l: "Over S$500/month", s: "The price of certainty is worth it" },
              { v: "wouldnt_pay", l: "I wouldn't pay for this kind of coverage", s: "Noted — genuinely useful to know" },
            ].map(o => (
              <OptionCard key={o.v} value={o.v} label={o.l} sub={o.s} selected={answers.endOfLifeInsurancePrice === o.v} onClick={() => set("endOfLifeInsurancePrice")(o.v)} />
            ))}
          </div>
        </div>

        {/* Emergency preparedness */}
        <div>
          <p className="font-sans text-sm mb-3">
            <strong>Emergency reality check:</strong> the average unplanned veterinary emergency in Singapore costs between S$3,000 and S$15,000. It happens fast, at the worst possible time.
          </p>
          <p className="font-grotesk text-[10px] uppercase tracking-widest opacity-50 mb-3">If that happened tomorrow — do you have a plan?</p>
          <div className="flex flex-col gap-2">
            {[
              { v: "yes_savings", l: "Yes — I have savings set aside for exactly this", s: "Prepared and at peace" },
              { v: "yes_card", l: "Yes — credit card / financing", s: "The modern safety net" },
              { v: "yes_family", l: "Yes — family would help", s: "The support system" },
              { v: "partially", l: "Partially — I'd manage, but it would be hard", s: "The honest answer for most people" },
              { v: "no_stressed", l: "No — and it's something I worry about", s: "This is exactly who Furbaby is for" },
              { v: "no_wouldnt", l: "No — and I'm not sure I could afford it", s: "We see you. This needs to change." },
            ].map(o => (
              <OptionCard key={o.v} value={o.v} label={o.l} sub={o.s} selected={answers.emergencyFinancialReady === o.v} onClick={() => set("emergencyFinancialReady")(o.v)} />
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}

/* ─── STEP 5: ABOUT YOU ──────────────────────────────────────────── */
function Step5({
  answers, set, toggle,
}: {
  answers: Answers;
  set: (k: keyof Answers) => (v: string) => void;
  toggle: (v: string) => void;
}) {
  return (
    <div>
      <p className="font-grotesk text-[10px] uppercase tracking-widest opacity-40 mb-4">Step 6 · About You</p>
      <h2 className="font-serif text-4xl md:text-5xl tracking-tighter leading-tight mb-3">
        Last few questions.<br />
        <span className="italic opacity-70">We promise.</span>
      </h2>
      <p className="font-sans text-sm opacity-50 leading-relaxed mb-10 max-w-sm">
        Almost done. {answers.petName ? `${answers.petName} will be very proud of you.` : "Your pet would be very proud of you."} We just need a couple more things.
      </p>

      <div className="flex flex-col gap-10">
        <div>
          <p className="font-grotesk text-[10px] uppercase tracking-widest opacity-50 mb-3">Where in Singapore are you based?</p>
          <div className="grid grid-cols-2 gap-2">
            {[
              "Holland Village / Buona Vista",
              "Tiong Bahru / Tanjong Pagar",
              "Orchard / River Valley",
              "East Coast / Marine Parade",
              "North — Ang Mo Kio / Bishan / Yishun",
              "West — Jurong / Clementi / Boon Lay",
              "Central — City Hall / Tanjong Pagar",
              "Somewhere else / Not sure yet",
            ].map(area => (
              <OptionCard
                key={area} value={area} label={area}
                selected={answers.singaporeArea === area}
                onClick={() => set("singaporeArea")(area)}
              />
            ))}
          </div>
        </div>

        <div>
          <p className="font-grotesk text-[10px] uppercase tracking-widest opacity-50 mb-3">If you could wave a magic wand and fix one thing about vet care — what would it be? <span className="opacity-60">(select all that apply)</span></p>
          <p className="font-sans text-xs opacity-35 mb-4">Be specific. Be honest. Be petty if you need to be.</p>
          <div className="flex flex-col gap-2 mb-4">
            {[
              { v: "remember_name", l: "Remember my pet's name — without checking the clipboard first", s: "You've met Biscuit nine times. You still ask." },
              { v: "estimate_first", l: "Give me a cost estimate before reaching for the scalpel", s: "The bill should not be a surprise. Ever." },
              { v: "explain_things", l: "Explain what you're doing — in actual words, not vibes", s: "A running commentary would be appreciated. We're right there." },
              { v: "follow_up", l: "Call to follow up after a procedure", s: "Revolutionary concept. Apparently not standard practice." },
              { v: "waiting_room", l: "Make the waiting room a place I don't want to escape", s: "Anxiety is contagious. Especially in small spaces with beige walls." },
              { v: "same_day", l: "Offer same-day appointments that aren't a production", s: "A 3-week waitlist for a limping cat is not ideal." },
              { v: "talk_to_pet", l: "Talk to my pet, not just about them", s: "They're right there. They can hear you." },
              { v: "stop_repeating", l: "Stop making me repeat their entire history at every visit", s: "You have a file. We've discussed this." },
              { v: "preventive", l: "Offer preventive care — not just reactive treatment", s: "Would love to know about problems before they become emergencies." },
              { v: "price_clarity", l: "Itemise the bill so I know what I'm actually paying for", s: "Line items. Revolutionary. Allegedly." },
              { v: "cheap_diagnosis", l: "Make diagnostics affordable — not $900 just to find out what's wrong", s: "The investigation shouldn't cost more than the treatment. Allegedly debatable." },
              { v: "outcome_clarity", l: "Be honest about whether the treatment will actually work", s: "We'd rather know upfront than spend a lot for no change. Uncertainty is expensive." },
            ].map(o => (
              <CheckCard key={o.v} value={o.v} label={o.l} sub={o.s} selected={answers.vetWishlist.includes(o.v)} onClick={() => toggle(o.v)} />
            ))}
          </div>
          <TextInput
            label="Something specific I need to get off my chest"
            value={answers.vetWishlistOther}
            onChange={set("vetWishlistOther")}
            placeholder="The floor. The smell. The hold music. Tell us everything."
            optional
          />
        </div>

        <div className="pt-4 border-t border-foreground/10">
          <p className="font-sans text-sm opacity-55 leading-relaxed mb-6">
            If you'd like to hear when Furbaby opens — or be first on the founding member waitlist — leave your email or WhatsApp below. No newsletters. No spam. Just the one message that matters.
          </p>
          <div className="flex flex-col gap-6">
            <TextInput
              label="Email address"
              value={answers.email}
              onChange={set("email")}
              placeholder="you@email.com"
              optional
            />
            <TextInput
              label="WhatsApp number"
              value={answers.whatsapp}
              onChange={set("whatsapp")}
              placeholder="+65 9123 4567"
              optional
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── THANK YOU ──────────────────────────────────────────────────── */
function ThankYou({ petName }: { petName: string }) {
  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col items-center justify-center px-6 text-center">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="max-w-lg"
      >
        <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-10">
          <Check className="w-8 h-8 text-primary-foreground" />
        </div>

        <h2 className="font-serif text-6xl md:text-7xl tracking-tighter mb-6">
          Thank you,<br />
          <span className="italic opacity-60">sincerely.</span>
        </h2>

        <p className="font-sans text-lg opacity-55 leading-relaxed mb-4">
          {petName
            ? `${petName} is lucky to have someone who cares enough to do a survey on their behalf.`
            : "Your pet is lucky to have someone who cares enough to do a survey on their behalf."}
        </p>
        <p className="font-sans text-sm opacity-40 leading-relaxed mb-12 max-w-sm mx-auto">
          Your responses will shape the clinic we build. We take that seriously. If you left your email, we'll reach out when the doors are ready to open — and only then.
        </p>

        <p className="font-grotesk text-xs uppercase tracking-widest opacity-30 mb-10">
          Furbaby Vet Care · Singapore · Est. Soon
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
          <Link
            href="/"
            className="group inline-flex items-center gap-2 font-grotesk text-xs uppercase tracking-widest hover:opacity-60 transition-opacity duration-500 border-b border-foreground/30 pb-1.5"
          >
            <ArrowLeft className="w-3 h-3" /> Back home
          </Link>
          <Link
            href="/membership"
            className="group inline-flex items-center gap-2 font-grotesk text-xs uppercase tracking-widest hover:opacity-60 transition-opacity duration-500 border-b border-foreground pb-1.5"
          >
            Explore membership <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform duration-400" />
          </Link>
        </div>
      </motion.div>
    </div>
  );
}

/* ─── FIELD HELPER ─────────────────────────────────────────────── */
function Field({
  label, value, onChange, placeholder, type = "text", required, maxLength,
}: {
  label: string; value: string;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  placeholder?: string; type?: string; required?: boolean; maxLength?: number;
}) {
  return (
    <div className="flex flex-col gap-2">
      <label className="font-grotesk text-[10px] uppercase tracking-widest opacity-50">{label}</label>
      <input
        type={type} value={value} onChange={onChange} required={required}
        placeholder={placeholder} maxLength={maxLength}
        className="bg-transparent border-b border-foreground/20 py-3 font-sans text-sm focus:outline-none focus:border-primary transition-colors duration-300 placeholder:opacity-25"
      />
    </div>
  );
}
